/**
 * 公開埋め込み面（公開ページ・ウィジェット iframe など）であるかを判定するユーティリティ。
 *
 * これらの面では、運営向けの「新しいバージョンが利用可能です」更新バナーを抑止する必要がある。
 * 理由:
 *  - エンドユーザーに対して運営向けの UI を見せない
 *  - Safari Private Mode 等で Cache / Service Worker / LocalStorage が使えない環境では、
 *    バナーの「今すぐ更新」を押してもキャッシュクリアが効かず、リロード後も同じスクリプト
 *    エラーが再発するため、バナーが永続表示されてしまう（無限ループ）
 *
 * NOTE: 同等の判定ロジックは React バンドル読込前に動く必要があるため `index.html` の
 *       インラインスクリプトにも複製している。条件を変更する場合は両方を必ず合わせること。
 */

const PUBLIC_EMBED_PATH_PREFIXES = ['/cg', '/widget'] as const;

/**
 * 現在のページが iframe 内で表示されているかを判定する。
 * クロスオリジン iframe では `window.top` アクセスが SecurityError を投げるが、
 * その場合も iframe 内とみなす（throw した時点で同一オリジンではない＝親が存在する）。
 */
export function isInIframe(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.top !== window.self;
  } catch {
    return true;
  }
}

/**
 * パスが公開埋め込み面（`/cg`, `/cg/*`, `/widget`, `/widget/*`）に該当するか。
 */
export function isPublicEmbedPath(pathname?: string): boolean {
  const p = pathname ?? (typeof window !== 'undefined' ? window.location?.pathname || '' : '');
  if (!p) return false;
  return PUBLIC_EMBED_PATH_PREFIXES.some(prefix => p === prefix || p.startsWith(`${prefix}/`));
}

/**
 * 運営向けの更新バナーを抑止するべきかどうか。
 *
 * - 公開埋め込みパス（/cg/*, /widget/*）
 * - もしくは何らかの iframe 内（将来的に別ルートを iframe 配信した場合の保険）
 */
export function shouldSuppressUpdateBanner(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return isPublicEmbedPath() || isInIframe();
  } catch {
    return false;
  }
}

/**
 * 開発用の補助ボタン（copy、エラー画面プレビューなど）。
 * ローカルの管理画面だけ出す。Staging・本番・公開ウィジェットでは出さない。
 */
export function shouldShowDevChrome(): boolean {
  const isLocal =
    import.meta.env.MODE === 'development' &&
    import.meta.env.VITE_STAGING_BUILD !== 'true';
  if (!isLocal || typeof window === 'undefined') return false;
  try {
    return !isPublicEmbedPath() && !isInIframe();
  } catch {
    return false;
  }
}
